import { motion } from "framer-motion";

const KPI = ({ icon, label, value, meta, metaClass = "" }) => (
  <div className="card p-5 flex flex-col gap-3">
    <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-gray-500 dark:text-gray-400 font-medium">
      <i className={`fas ${icon} text-primary-500`} />
      {label}
    </div>
    <div className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 font-mono leading-none">
      {value}
    </div>
    <div className={`text-xs text-gray-500 dark:text-gray-400 ${metaClass}`}>
      {meta}
    </div>
  </div>
);

const FeedRow = ({ initials, gradient, body, tag, tagClass, time }) => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-700/20 transition-colors">
    <div
      className={`w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-semibold ${gradient}`}
    >
      {initials}
    </div>
    <div className="flex-1 text-sm text-gray-200">{body}</div>
    {tag && (
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${tagClass}`}
      >
        {tag}
      </span>
    )}
    <span className="text-[11px] text-gray-500 font-mono">{time}</span>
  </div>
);

const OnlineRow = ({ initials, gradient, name, meta, time }) => (
  <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-700/20 transition-colors">
    <span className="relative flex w-2 h-2">
      <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60 animate-ping" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
    </span>
    <div
      className={`w-7 h-7 rounded-lg flex items-center justify-center text-white text-[10px] font-semibold ${gradient}`}
    >
      {initials}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-gray-100 truncate">{name}</div>
      <div className="text-[11px] text-gray-500 font-mono truncate">{meta}</div>
    </div>
    <span className="text-[11px] text-green-400 font-mono">{time}</span>
  </div>
);

const ExpireRow = ({ name, sub, days, level = "soon" }) => {
  const tone =
    level === "urgent"
      ? "bg-red-500/15 text-red-400"
      : "bg-yellow-500/15 text-yellow-400";

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          level === "urgent"
            ? "bg-red-500/15 text-red-400"
            : "bg-yellow-500/15 text-yellow-400"
        }`}
      >
        <i className="fas fa-clock text-xs" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-100">{name}</div>
        <div className="text-[11px] text-gray-500 font-mono">{sub}</div>
      </div>
      <span
        className={`text-xs font-mono font-semibold px-2 py-1 rounded-md ${tone}`}
      >
        {days}
      </span>
    </div>
  );
};

const ResellerDashboardPreview = () => (
  <div className="relative">
    <div className="mx-auto space-y-6 relative z-10">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Dashboard
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage your devices and playlists for streaming
        </p>
      </div>

      {/* Tier progression (compact version of the existing slider) */}
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="card p-6 shadow-md"
        initial={{ opacity: 0, y: 20 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-500/20 border border-primary-500/40 flex items-center justify-center">
              <i className="fas fa-seedling text-primary-400" />
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-100">Nano</div>
              <div className="text-[11px] text-gray-500 font-mono">
                You're here · 2.01% off
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-100">Starter</div>
              <div className="text-[11px] text-gray-500 font-mono">
                23 customers away · 5.03% off
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-dashed border-blue-500/40 flex items-center justify-center">
              <i className="fas fa-rocket text-blue-400" />
            </div>
          </div>
        </div>
        <div className="relative h-2 rounded-full bg-gray-700/40 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: "55%",
              background: "linear-gradient(90deg, #7c3aed, #8b5cf6, #3b82f6)",
              boxShadow: "0 0 16px rgba(139, 92, 246, 0.5)",
            }}
          />
        </div>
        <div className="flex justify-between mt-2 text-[11px] text-gray-500 font-mono">
          <span>25</span>
          <span>30</span>
          <span>35</span>
          <span>40</span>
          <span>45</span>
          <span>51</span>
        </div>
      </motion.div>

      {/* KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI
          icon="fa-users"
          label="Customers"
          meta={
            <>
              <span className="text-green-400 font-medium">↑ +3</span> this week
            </>
          }
          value="28"
        />
        <KPI
          icon="fa-broadcast-tower"
          label="Online Now"
          meta={
            <>
              <span className="inline-flex w-2 h-2 rounded-full bg-green-400 animate-pulse mr-1" />{" "}
              across 8 devices
            </>
          }
          value="5"
        />
        <KPI
          icon="fa-tv"
          label="Active Devices"
          meta={
            <>
              <span className="text-green-400 font-medium">↑ +12</span> in 7
              days
            </>
          }
          value="142"
        />
        <KPI
          icon="fa-key"
          label="LunaKeys™"
          meta={
            <>
              ~5/wk burn ·{" "}
              <span className="text-yellow-400 font-medium">20 wk runway</span>
            </>
          }
          value="100"
        />
        <KPI
          icon="fa-exclamation-triangle"
          label="Expiring Soon"
          meta={
            <>
              in next 7 days ·{" "}
              <span className="text-red-400 font-medium">act now</span>
            </>
          }
          value="3"
        />
      </div>

      {/* Middle row: activity (2/3) + online/expiring (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-6 shadow-md lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-100 flex items-center gap-2">
              <i className="fas fa-bolt text-primary-400" /> Live Activity
            </h3>
            <span className="text-[11px] text-gray-500 font-mono uppercase tracking-wider">
              last 24h · auto-refresh
            </span>
          </div>
          <div className="space-y-1">
            <FeedRow
              body={
                <>
                  <b className="text-gray-100">elchino</b>
                  <span className="text-gray-400">
                    {" "}
                    opened Lunova Player on Fire TV (AFTBOXE1)
                  </span>
                </>
              }
              gradient="bg-gradient-to-br from-green-400 to-emerald-600"
              initials="EC"
              tag="live"
              tagClass="bg-green-500/20 text-green-400"
              time="2m"
            />
            <FeedRow
              body={
                <>
                  <b className="text-gray-100">DarkM92</b>
                  <span className="text-gray-400">
                    {" "}
                    logged in on a new device — Pixel 8 Pro
                  </span>
                </>
              }
              gradient="bg-gradient-to-br from-yellow-400 to-orange-500"
              initials="DM"
              tag="new"
              tagClass="bg-primary-500/20 text-primary-400"
              time="12m"
            />
            <FeedRow
              body={
                <>
                  <b className="text-gray-100">kgrogan057</b>
                  <span className="text-gray-400">
                    {" "}
                    hit 10/10 device limit · consider upgrading
                  </span>
                </>
              }
              gradient="bg-gradient-to-br from-primary-500 to-primary-700"
              initials="KG"
              tag="limit"
              tagClass="bg-yellow-500/20 text-yellow-400"
              time="1h"
            />
            <FeedRow
              body={
                <>
                  <b className="text-gray-100">vibecoder</b>
                  <span className="text-gray-400">
                    {" "}
                    subscribed via your reseller link · +6 devices
                  </span>
                </>
              }
              gradient="bg-gradient-to-br from-red-400 to-rose-600"
              initials="VB"
              tag="new customer"
              tagClass="bg-green-500/20 text-green-400"
              time="3h"
            />
            <FeedRow
              body={
                <>
                  <b className="text-gray-100">JustAGuy</b>
                  <span className="text-gray-400">
                    {" "}
                    renewed 12-month plan · +12mo to expiry
                  </span>
                </>
              }
              gradient="bg-gradient-to-br from-blue-500 to-indigo-700"
              initials="JG"
              tag="renew"
              tagClass="bg-blue-500/20 text-blue-400"
              time="5h"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="card p-5 shadow-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-100 flex items-center gap-2 text-sm">
                <i className="fas fa-circle text-green-400 text-[10px]" />
                Online Now
              </h3>
              <span className="text-[11px] text-gray-500 font-mono">
                5 / 28
              </span>
            </div>
            <div className="space-y-1">
              <OnlineRow
                gradient="bg-gradient-to-br from-green-400 to-emerald-600"
                initials="EC"
                meta="Fire TV · AFTBOXE1 · 17m"
                name="elchino"
                time="17m"
              />
              <OnlineRow
                gradient="bg-gradient-to-br from-yellow-400 to-orange-500"
                initials="DM"
                meta="Pixel 8 Pro"
                name="DarkM92"
                time="2m"
              />
              <OnlineRow
                gradient="bg-gradient-to-br from-primary-500 to-primary-700"
                initials="KG"
                meta="AFTBOXE1"
                name="kgrogan057"
                time="4m"
              />
              <OnlineRow
                gradient="bg-gradient-to-br from-red-400 to-rose-600"
                initials="HC"
                meta="Android TV 9"
                name="heachico"
                time="38s"
              />
              <OnlineRow
                gradient="bg-gradient-to-br from-blue-500 to-indigo-700"
                initials="DV"
                meta="Web · Chrome"
                name="dev"
                time="1m"
              />
            </div>
          </div>

          <div className="card p-5 shadow-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-100 flex items-center gap-2 text-sm">
                <i className="fas fa-stopwatch text-yellow-400" /> Expiring Soon
              </h3>
              <span className="text-[11px] text-gray-500 font-mono">
                14 days
              </span>
            </div>
            <div className="space-y-1">
              <ExpireRow
                days="3 days"
                level="urgent"
                name="El Chino"
                sub="elchino · gotdark.org"
              />
              <ExpireRow
                days="9 days"
                level="soon"
                name="MixDoctor"
                sub="mixdoctor · gotdark.org"
              />
              <ExpireRow
                days="14 days"
                level="soon"
                name="JustAGuy"
                sub="mtvp26 · m1pro1.com"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Playlists table */}
      <div className="card shadow-md overflow-hidden">
        <div className="p-5 flex items-center gap-3 border-b border-gray-700/40">
          <h2 className="text-lg font-bold text-gray-100">Playlists</h2>
          <div className="flex-1 max-w-md">
            <div className="input flex items-center gap-2 !py-2">
              <i className="fas fa-search text-gray-500 text-xs" />
              <input
                className="flex-1 bg-transparent border-0 outline-none text-sm text-gray-100 placeholder-gray-500"
                placeholder="Search playlists by name, username, etc…"
              />
            </div>
          </div>
          <div className="badge badge-secondary !rounded-lg !px-3 !py-1.5">
            <i className="fas fa-users mr-2" /> 28
          </div>
          <button className="btn btn-ghost">
            <i className="fas fa-filter" /> Services
          </button>
          <button className="btn btn-primary !rounded-lg">
            <i className="fas fa-plus" /> Add Playlist
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Playlist</th>
                <th className="table-header">Domain</th>
                <th className="table-header">Username</th>
                <th className="table-header">Devices</th>
                <th className="table-header">LunaKeys™</th>
                <th className="table-header">Status</th>
                <th className="table-header">Expires</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[
                {
                  name: "kgrogan057@g…",
                  playlist: "dm",
                  domain: "gotdark.org",
                  user: "kgrogan057",
                  devices: 10,
                  keys: 12,
                  status: "online",
                  expires: "2025-05-21",
                },
                {
                  name: "DM",
                  playlist: "DM",
                  domain: "gotdark.org",
                  user: "dev",
                  devices: 7,
                  keys: 8,
                  status: "online",
                  expires: "2025-05-21",
                },
                {
                  name: "GXPRO1",
                  playlist: "GXPRO1",
                  domain: "gxpro1.com",
                  user: "DarkM92",
                  devices: 7,
                  keys: 4,
                  status: "offline 14h",
                  expires: "2026-01-04",
                },
                {
                  name: "vibecoder",
                  playlist: "test playlist",
                  domain: "gotdark.org",
                  user: "vibecoder",
                  devices: 6,
                  keys: 6,
                  status: "online",
                  expires: "2026-02-18",
                },
                {
                  name: "El Chino",
                  playlist: "El Chino",
                  domain: "gotdark.org",
                  user: "elchino",
                  devices: 2,
                  keys: 1,
                  status: "online",
                  expires: "3 days",
                  expiresUrgent: true,
                },
              ].map((row, i) => (
                <tr
                  className={`${
                    i % 2 === 0
                      ? "table-row-striped-even"
                      : "table-row-striped-odd"
                  } table-row-hover`}
                  key={row.name}
                >
                  <td className="table-cell font-medium">{row.name}</td>
                  <td className="table-cell text-gray-400">{row.playlist}</td>
                  <td className="table-cell text-gray-400 font-mono text-xs">
                    {row.domain}
                  </td>
                  <td className="table-cell font-mono">{row.user}</td>
                  <td className="table-cell">
                    <span className="badge badge-primary !px-2.5">
                      <i className="fas fa-tv mr-1.5 text-[10px]" />
                      {row.devices}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className="badge badge-info !px-2.5">
                      <i className="fas fa-ticket-alt mr-1.5 text-[10px]" />
                      {row.keys}
                    </span>
                  </td>
                  <td className="table-cell">
                    {row.status.startsWith("online") ? (
                      <span className="inline-flex items-center gap-1.5 text-green-400 text-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        {row.status}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-xs">
                        {row.status}
                      </span>
                    )}
                  </td>
                  <td className="table-cell">
                    {row.expiresUrgent ? (
                      <span className="badge badge-danger !px-2.5">
                        {row.expires}
                      </span>
                    ) : (
                      <span className="text-gray-400 font-mono text-xs">
                        {row.expires}
                      </span>
                    )}
                  </td>
                  <td className="table-cell">
                    <div className="flex gap-1.5">
                      <button className="w-7 h-7 rounded-md border border-gray-700/60 text-primary-400 hover:bg-primary-500/10 transition-colors">
                        <i className="fas fa-edit text-xs" />
                      </button>
                      <button className="w-7 h-7 rounded-md border border-gray-700/60 text-orange-400 hover:bg-orange-500/10 transition-colors">
                        <i className="fas fa-sign-out-alt text-xs" />
                      </button>
                      <button className="w-7 h-7 rounded-md border border-gray-700/60 text-red-400 hover:bg-red-500/10 transition-colors">
                        <i className="fas fa-trash text-xs" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
);

export default ResellerDashboardPreview;
